import { Component, DestroyRef, Input, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import { UtilService } from 'src/app/util.service';
import { EntryService } from '../entry.service';
import { PlatformService } from 'src/app/platform.service';
import { EntryData, ProviderData } from '../structure-data.models';
import { combineLatest, map } from 'rxjs';
import { EntrySelectors } from 'src/app/state/entry.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfigService } from 'src/app/config.service';
import { CommonService } from 'src/app/common.service';
import { ProviderCacheService } from '../ProviderCacheService';
import { EntryActions } from 'src/app/state/entry.actions';
@Component({
  selector: 'entry-page-footer',
  templateUrl: './entry-page-footer.component.html',
  styleUrl: './entry-page-footer.component.css',
  standalone: false
})
export class EntryPageFooterComponent {
  @Input() isEntryPage: boolean;
  public accession: string;
  public entryData = signal<EntryData>({} as EntryData);

  private readonly destroyRef = inject(DestroyRef);
  public providerData: ProviderData;

  constructor(
    private store: Store,
    public gaService: GoogleAnalyticsService,
    private configService: ConfigService,
    private commonService: CommonService,
    private providerCache: ProviderCacheService,
    private platform: PlatformService,
  ) {}
  async ngOnInit(): Promise<void> {  
    if(this.isEntryPage){
      combineLatest([
        this.store.select(EntrySelectors.entryData).pipe()
      ])
      .pipe(
        map(([entryData]) => {
          this.entryData.set(entryData)
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  
      if(this.entryData() && this.entryData().providerId){
        this.getProviderData();
      }
      this.accession = this.entryData().uniprotAccession;
    } 
  }

  public getProviderData(): void {
    const providerId = this.entryData().providerId;
    // 1. Try cached value
    if(this.platform.isBrowserPlatform()){
      const cachedData = this.providerCache.get(providerId);
      if (cachedData) {
        this.providerData = cachedData;
        this.store.dispatch(
          EntryActions.setCurrentProvider({
            providerData: this.providerData
          })
        );
        return;
      }

      // 2. Otherwise, call API
      const auth = this.configService.getConfig().apiKey
        ? `?key=${this.configService.getConfig().apiKey}`
        : '';
      const apiUrl =
        this.configService.getConfig().providerApiUrl + providerId + auth;
      this.commonService.getApiData(apiUrl, { isInternal: true }).subscribe(
        (data) => {
          this.providerData = {
            providerName: data.providerName,
            toolUsed: this.entryData().toolUsed,
            providerUrl: data.providerUrl,
            feedback: data.feedback,
            license: data.license,
            citations: data.citations,
            copyrights: data.copyrights,
            modelCreatedDate: this.entryData().modelCreatedDate
          };
          this.store.dispatch(
            EntryActions.setCurrentProvider({
              providerData: this.providerData
            })
          );
          // Save in cache
          this.providerCache.set(providerId, this.providerData);
        },
        (error) => {
          this.providerData = {
            providerName : 'Google DeepMind',
            toolUsed: this.entryData().toolUsed,
            providerUrl: 'https://deepmind.com',
            feedback: 'alphafold@deepmind.com',
            license: 'CC-BY-4.0',
            citations: [{
              "description": "Jumper, J et al. Highly accurate protein structure prediction with AlphaFold. Nature (2021)",
              "url": "https://www.nature.com/articles/s41586-021-03819-2",
              "category": "AlphaFold"
            }],
            copyrights: [
              "AlphaFold Data Copyright (2022) DeepMind Technologies Limited.",
              "AlphaMissense Copyright (2023) DeepMind Technologies Limited."
            ],
            modelCreatedDate: this.entryData().modelCreatedDate
          };
          this.store.dispatch(
            EntryActions.setCurrentProvider({
              providerData: this.providerData
            })
          );
          this.providerCache.set(providerId, this.providerData);
        }
      );
    }
  }
}
